import { Api } from '@axiom/ui';
import type {
  Candidate,
  CandidateOpportunity,
  CandidateOpportunityOnboarding,
  CandidatesHiddenOpportunity,
  CandidateWorkFeedItem,
  ExpressInterest,
  Opportunity,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';

import type { PatchOpportunityByCandidateRequest } from './patch-opportunity-by-candidate-request-api-schema';

class CandidateOpportunitiesApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiPortalUrl,
    });
  }

  readCandidateWorkFeedItem({
    candidateId,
    opportunityId,
  }: {
    candidateId: Candidate['id'];
    opportunityId: Opportunity['id'];
  }) {
    return super.read<{ data: CandidateWorkFeedItem }>({
      endpoint: `/candidates/${candidateId}/opportunities/${opportunityId}`,
      method: 'GET',
    });
  }

  async createWorkFeedHidden({
    candidateId,
    body,
  }: {
    candidateId: Candidate['id'];
    body: CandidatesHiddenOpportunity;
  }) {
    return super.write<{ data: CandidateOpportunity }>({
      endpoint: `/candidates/${candidateId}/opportunities/hidden`,
      body,
      method: 'POST',
    });
  }

  async createWorkFeedInterested({
    candidateId,
    body,
  }: {
    candidateId: Candidate['id'];
    body: ExpressInterest;
  }) {
    return super.write<{ data: CandidateOpportunity }>({
      endpoint: `/candidates/${candidateId}/opportunities/interested`,
      body,
      method: 'POST',
    });
  }

  async refreshCandidateWorkFeedItem({
    candidateId,
    opportunityId,
  }: {
    candidateId: Candidate['id'];
    opportunityId: Opportunity['id'];
  }) {
    return super.invalidate(
      `/candidates/${candidateId}/opportunities/${opportunityId}`
    );
  }

  async updateEngagementOptOut({
    candidateId,
    opportunityId,
    body,
  }: {
    candidateId: Candidate['id'];
    opportunityId: Opportunity['id'];
    body: Partial<PatchOpportunityByCandidateRequest>;
  }) {
    return super.write<{ data: never }>({
      endpoint: `/candidates/${candidateId}/opportunities/${opportunityId}/engagementOptOut`,
      body,
      method: 'PATCH',
    });
  }

  async updateWarming({
    candidateId,
    opportunityId,
    body,
  }: {
    candidateId: Candidate['id'];
    opportunityId: Opportunity['id'];
    body: Partial<PatchOpportunityByCandidateRequest>;
  }) {
    return super.write<{ data: never }>({
      endpoint: `/candidates/${candidateId}/opportunities/${opportunityId}/warming`,
      body,
      method: 'PATCH',
    });
  }

  readCandidateOpportunityOnboarding({
    candidateId,
    opportunityId,
  }: {
    candidateId: Candidate['id'];
    opportunityId: Opportunity['id'];
  }) {
    return super.read<{ data: CandidateOpportunityOnboarding }>({
      endpoint: `/candidates/${candidateId}/opportunities/${opportunityId}/onboarding`,
      method: 'GET',
    });
  }
}

export const CandidateOpportunitiesApi = new CandidateOpportunitiesApiClass();
